/** @format */

import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { LogsheetService } from "src/app/services/logsheet/logsheet.service";
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service";
import { LoaderService } from "src/app/services/loader/loader.service";
import { ToastService } from "src/app/services/toast/toast.service";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { UserService } from "src/app/services/user/user.service";
import {
  NotificationConfigService,
  CreateNotificationConfig,
  UpdateNotificationConfig,
  NotificationRecipient,
} from "src/app/services/notification-config/notification-config.service";
import { NotificationChannels } from "src/app/components/notification-type-card/notification-type-card.component";

type RecipientCardKey = "perItem" | "formSubmission" | "reminder";

interface RecipientCardState {
  expanded: boolean;
  appUserSelected: boolean;
  uploadedRefFileSelected: boolean;
  selectedUsers: any[];
  uploadedFileName: string;
  csvValidated: boolean;
  validationMessage: string;
  uploadProgress: number;
  uploadStatus: "idle" | "uploading" | "complete" | "error";
  showUploadStatus: boolean;
  uploadedCsvData: any[];
  appUserCsvData: any[];
  uploadProgressInterval: ReturnType<typeof setInterval> | null;
  uploadCompleteTimeout: ReturnType<typeof setTimeout> | null;
}

@Component({
  standalone: false,
  selector: "app-notifications-config",
  templateUrl: "./notifications-config.component.html",
  styleUrls: ["./notifications-config.component.css"],
})
export class NotificationsConfigComponent implements OnInit {
  recipientCards: Record<RecipientCardKey, RecipientCardState> = {
    perItem: {
      expanded: true,
      appUserSelected: false,
      uploadedRefFileSelected: false,
      selectedUsers: [],
      uploadedFileName: "",
      csvValidated: false,
      validationMessage: "",
      uploadProgress: 0,
      uploadStatus: "idle",
      showUploadStatus: false,
      uploadedCsvData: [],
      appUserCsvData: [],
      uploadProgressInterval: null,
      uploadCompleteTimeout: null,
    },
    formSubmission: {
      expanded: false,
      appUserSelected: false,
      uploadedRefFileSelected: false,
      selectedUsers: [],
      uploadedFileName: "",
      csvValidated: false,
      validationMessage: "",
      uploadProgress: 0,
      uploadStatus: "idle",
      showUploadStatus: false,
      uploadedCsvData: [],
      appUserCsvData: [],
      uploadProgressInterval: null,
      uploadCompleteTimeout: null,
    },
    reminder: {
      expanded: false,
      appUserSelected: false,
      uploadedRefFileSelected: false,
      selectedUsers: [],
      uploadedFileName: "",
      csvValidated: false,
      validationMessage: "",
      uploadProgress: 0,
      uploadStatus: "idle",
      showUploadStatus: false,
      uploadedCsvData: [],
      appUserCsvData: [],
      uploadProgressInterval: null,
      uploadCompleteTimeout: null,
    },
  };
  selectedValues = {
    company: {
      companyId: 0,
      companyName: "",
    },
    plant: {
      plantId: 0,
      plantName: "",
    },
    unit: {
      unitId: 0,
      unitName: "",
    },
  };

  title: string = "Notifications Configuration";
  dbUsers: any[] = [];
  selectedRecipients: any[] = [];
  // state for checkboxes
  whatsappSelected = false;
  smsSelected = false;
  emailSelected = false;
  // Keep the original user list to preserve phone numbers
  originalUserList: any[] = [];
  logsheetName: string = "";
  logsheetId: number = 0;
  activeStep = 1;
  logsheet: any = {};
  selectDataPoints: any = [];
  firstRender = false;
  selectedUsers: any[] = [];
  showSaveModal: boolean = false;
  isEditMode: boolean = false;
  existingConfigId: number = 0;
  existingConfigIds: number[] = []; // Store ALL config IDs for deletion
  configLoaded: boolean = false; // Flag to prevent double-loading notification config

  // Enable/disable toggle per type
  perItemEnabled = false;
  formSubmissionEnabled = false;
  reminderEnabled = false;

  // Channel states per type
  perItemChannels: NotificationChannels = {
    sms: false,
    // whatsapp: false,
    email: false,
    whatsapp: undefined,
  };
  formSubmissionChannels: NotificationChannels = {
    sms: false,
    // whatsapp: false,
    email: false,
    whatsapp: undefined,
  };
  reminderChannels: NotificationChannels = {
    sms: false,
    // whatsapp: false,
    email: false,
    whatsapp: undefined,
  };

  // Tracks the channel selections for that notification type
  notificationTypeChannels: NotificationChannels = {
    sms: false,
    // whatsapp: false,
    email: false,
    whatsapp: undefined,
  };
  selectedNotificationType: any;
  appUserSelected: any;
  uploadedRefFileSelected: any;
  uploadedFileName: any;
  csvValidated: boolean = false;
  uploadedCsvData: any;
  appUserCsvData: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private logsheetService: LogsheetService,
    private errorService: ErrorHandlerService,
    private loaderService: LoaderService,
    public toastService: ToastService,
    private userService: UserService,
    private notificationConfigService: NotificationConfigService,
  ) {}

  // TRUE when at least one is selected
  get isStep1Valid(): boolean {
    const cards = [
      { enabled: this.perItemEnabled, channels: this.perItemChannels },
      {
        enabled: this.formSubmissionEnabled,
        channels: this.formSubmissionChannels,
      },
      { enabled: this.reminderEnabled, channels: this.reminderChannels },
    ];

    return cards.some(
      (card) => card.enabled && (card.channels.sms || card.channels.email),
    );
  }

  get isStep2Valid(): boolean {
    const enabledKeys: RecipientCardKey[] = [];
    if (this.perItemEnabled) enabledKeys.push("perItem");
    if (this.formSubmissionEnabled) enabledKeys.push("formSubmission");
    if (this.reminderEnabled) enabledKeys.push("reminder");

    if (enabledKeys.length === 0) {
      return false;
    }

    return enabledKeys.every((key) => {
      let check = true;
      const card = this.recipientCards[key];
      // console.log(`Validating card ${key}: card.csvValidated=${card.csvValidated}, validationMessage='${card.validationMessage}', selectedUsers.length=${card.selectedUsers.length}`);
      if (
        card.uploadedRefFileSelected &&
        !(
          card.csvValidated &&
          (card.validationMessage === "" ||
            card.validationMessage === "File uploaded successfully.")
        )
      ) {
        check = false;
      }
      if (card.appUserSelected && !(card.selectedUsers.length > 0)) {
        check = false;
      }
      if (!card.appUserSelected && !card.uploadedRefFileSelected) {
        check = false;
      }

      return check;
    });
  }

  onWhatsappChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.whatsappSelected = input.checked;
  }

  onSmsChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.smsSelected = input.checked;
  }

  onEmailChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.emailSelected = input.checked;
  }

  // Called whenever the child emits updated channel selections
  onChannelsChange(channels: NotificationChannels) {
    // Update parent state
    this.smsSelected = channels.sms;
    // this.whatsappSelected = channels.whatsapp;
    this.emailSelected = channels.email;
  }

  // Called when the "notification type" checkbox (title) is toggled
  onNotificationTypeToggle(type: string, enabled: boolean) {
    switch (type) {
      case "perItem":
        this.perItemEnabled = enabled;
        if (!enabled)
          this.perItemChannels = {
            sms: false,
            email: false,
            whatsapp: undefined,
          };
        break;
      case "formSubmission":
        this.formSubmissionEnabled = enabled;
        if (!enabled)
          this.formSubmissionChannels = {
            sms: false,
            // whatsapp: false,
            email: false,
            whatsapp: undefined,
          };
        break;
      case "reminder":
        this.reminderEnabled = enabled;
        if (!enabled)
          this.reminderChannels = {
            sms: false,
            email: false,
            whatsapp: undefined,
          };
        break;
    }

    this.ensureFirstRecipientCardExpanded();
  }

  private ensureFirstRecipientCardExpanded(): void {
    const order: RecipientCardKey[] = ["perItem", "formSubmission", "reminder"];
    const enabledMap: Record<RecipientCardKey, boolean> = {
      perItem: this.perItemEnabled,
      formSubmission: this.formSubmissionEnabled,
      reminder: this.reminderEnabled,
    };

    let firstEnabled: RecipientCardKey | null = null;
    for (const key of order) {
      if (enabledMap[key]) {
        firstEnabled = key;
        break;
      }
    }

    order.forEach((key) => {
      this.recipientCards[key].expanded = key === firstEnabled;
    });
  }

  // Called when the child emits channel changes for the selected notification type
  onNotificationTypeChannelsChange(
    type: string,
    channels: NotificationChannels,
  ) {
    switch (type) {
      case "perItem":
        this.perItemChannels = { ...channels };
        break;
      case "formSubmission":
        this.formSubmissionChannels = { ...channels };
        break;
      case "reminder":
        this.reminderChannels = { ...channels };
        break;
    }
  }

  private resetUploadProgress(card: RecipientCardState): void {
    if (card.uploadProgressInterval) {
      clearInterval(card.uploadProgressInterval);
      card.uploadProgressInterval = null;
    }
    if (card.uploadCompleteTimeout) {
      clearTimeout(card.uploadCompleteTimeout);
      card.uploadCompleteTimeout = null;
    }
    card.uploadProgress = 0;
    card.uploadStatus = "idle";
    card.showUploadStatus = false;
  }

  private startUploadProgress(card: RecipientCardState): void {
    this.resetUploadProgress(card);
    card.showUploadStatus = true;
    card.uploadStatus = "uploading";
    card.uploadProgress = 0;

    card.uploadProgressInterval = setInterval(() => {
      if (card.uploadProgress < 95) {
        card.uploadProgress = Math.min(95, card.uploadProgress + 5);
      }
    }, 350);
  }

  private completeUploadProgress(card: RecipientCardState): void {
    if (card.uploadProgressInterval) {
      clearInterval(card.uploadProgressInterval);
      card.uploadProgressInterval = null;
    }
    card.uploadProgress = 100;
    card.uploadStatus = "complete";

    card.uploadCompleteTimeout = setTimeout(() => {
      this.resetUploadProgress(card);
    }, 5000);
  }

  private failUploadProgress(card: RecipientCardState): void {
    if (card.uploadProgressInterval) {
      clearInterval(card.uploadProgressInterval);
      card.uploadProgressInterval = null;
    }
    card.uploadStatus = "error";
    card.showUploadStatus = false;
  }

  toggleCard(cardKey: RecipientCardKey): void {
    this.recipientCards[cardKey].expanded =
      !this.recipientCards[cardKey].expanded;
  }

  toggleAppUser(cardKey: RecipientCardKey): void {
    const card = this.recipientCards[cardKey];
    card.appUserSelected = !card.appUserSelected;
    if (!card.appUserSelected) {
      card.selectedUsers = [];
      card.appUserCsvData = [];
    }
  }

  toggleUploadedRefFile(cardKey: RecipientCardKey): void {
    this.recipientCards[cardKey].uploadedRefFileSelected =
      !this.recipientCards[cardKey].uploadedRefFileSelected;
  }

  onCancel(): void {
    // Navigate to Forms submenu (logsheets) under settings
    this.router.navigate(["/settings/forms"]);
  }

  ngOnInit(): void {
    // Prefer navigation state (set by LogsheetComponent.transferData)
    const navState: any = history.state;
    this.selectedValues = navState?.selectedValues;
    this.loadExistingLogsheet();
    //CHECK if we have a valid Plant ID. If so, fetch the users.
    if (
      this.selectedValues &&
      this.selectedValues.plant &&
      this.selectedValues.plant.plantId
    ) {
      // We create the request object exactly how your Service expects it
      const userRequest = {
        pageNumber: 1,
        pageSize: 1000, // We ask for 1000 so we get everyone (since dropdown handles filtering)
        search: "",
        plantId: this.selectedValues.plant.plantId,
      };

      this.loadUsers(userRequest);
    }
}

loadUsers(userRequest: any): void {
      // Call your existing service
      this.userService.retrieveUsers(userRequest).subscribe({
        next: (response: any) => {
          //The list is inside 'item1'
          const userList = response.item1 || [];

          if (Array.isArray(userList)) {
            // Store original list for reference
            this.originalUserList = userList;

            // Map the API fields to the fields our Dropdown expects (id and name)
            // Also preserve full user object for later access to phone numbers
            this.dbUsers = userList.map((u: any) => ({
              id: u.subjectId || u.userSubjectId || u.userId,
              userSubjectId: u.subjectId || u.userSubjectId,
              subjectId: u.subjectId || u.userSubjectId,
              name: u.firstName + " " + u.lastName, // Combine names
              firstName: u.firstName,
              lastName: u.lastName,
              email: u.email,
              phone:
                u.phone ||
                u.phoneNumber ||
                u.contactNumber ||
                u.mobileNumber ||
                "", // Try multiple field names
              userId: u.userId,
              ...u, // Spread all properties to ensure we capture everything
            }));

            // If logsheet ID is already available, trigger notification config loading
            // Use the safe loading method to prevent double-loading
            this.loadNotificationConfigIfReady();
          } else {
            console.error("Still not an array!", userList);
            this.dbUsers = [];
          }
        },
        error: (err) => {
          console.error("Error fetching users:", err);
          // Optional: Show an error message using your errorService
          this.errorService.handleError?.(err, "Error retrieving users");
        },
      });
}

  loadExistingLogsheet() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get("id");
      if (id) {
        this.logsheetService.retrieveLogSheetById(id).subscribe({
          next: (resp: any) => {
            this.logsheetName = resp["logSheetName"] ?? "";
            this.logsheetId = resp["logSheetTemplateId"] ?? 0;

            let output: any = {};
            Object.keys(resp).forEach((key) => {
              output[key] = resp[key];
            });
            this.logsheet = { ...output };

            // Get form fields from LogsheetService if available
            if (Object.keys(this.logsheet).length != 0) {
              for (const dataPoint of this.logsheet["dataPointDetails"]) {
                // Possible processing per datapoint
                this.selectDataPoints = [
                  ...this.selectDataPoints,
                  dataPoint["dataPointName"],
                ];
              }
            }
            if (this.logsheetId) {
              // Load notification config after we know the logsheet ID
              // This will be called after users are loaded, or immediately if users are already available
              this.loadNotificationConfigWhenReady();
            }
          },
          error: (err: any) => {
            this.errorService.handleError?.(
              err,
              "Error retrieving logsheet name",
            );
            console.error("Error retrieving logsheet name", err);
          },
        });
      }
    });

    return;
  }

  /**
   * Ensures users are loaded before attempting to load notification config
   * This is necessary because selectedUsers matching requires dbUsers to be populated
   */
  private loadNotificationConfigWhenReady(): void {
    // If users are already loaded, load config immediately
    if (this.dbUsers.length > 0) {
      this.loadNotificationConfigIfReady();
    } else if (this.selectedValues?.plant?.plantId) {
      // Users are still loading, wait for them to load first
      // Create a subscription that waits for users to be loaded
      const checkInterval = setInterval(() => {
        if (this.dbUsers.length > 0 && !this.configLoaded) {
          clearInterval(checkInterval);
          this.loadNotificationConfigIfReady();
        }
      }, 100); // Check every 100ms
      
      // Safety timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.configLoaded) {
          console.warn('Timeout waiting for users to load, loading config anyway');
          this.loadNotificationConfigIfReady();
        }
      }, 5000);
    } else {
      // No plant selected, load config without user matching
      this.loadNotificationConfigIfReady();
    }
  }

  /**
   * Loads notification config only if not already loaded
   */
  private loadNotificationConfigIfReady(): void {
    if (!this.configLoaded && this.logsheetId > 0) {
      this.configLoaded = true;
      this.loadExistingNotificationConfig(this.logsheetId);
    }
  }

  private getUserSubjectId(user: any): string {
    return String(user?.userSubjectId || user?.subjectId || "").trim();
  }

  private getUserDisplayName(user: any): string {
    return String(
      user?.name ||
      user?.userName ||
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
    ).trim();
  }

  private getUserEmail(user: any): string {
    return String(user?.email || user?.recipient1Contact || "").trim();
  }

  private findOriginalUser(user: any): any {
    const subjectId = this.getUserSubjectId(user);
    return this.originalUserList.find((u: any) => {
      const originalSubjectId = String(u?.subjectId || u?.userSubjectId || "").trim();
      return subjectId
        ? originalSubjectId === subjectId
        : u?.userId === user?.userId || u?.userId === user?.id;
    });
  }

  private isApplicationUserRow(row: any): boolean {
    return row?.__recipientSource === "applicationUser" || !!row?.userSubjectId;
  }

  private findSelectedUserForRecipientRow(row: any, selectedUsers: any[]): any {
    const rowSubjectId = String(row?.userSubjectId || "").trim();
    const rowName = String(row?.userName || row?.["Recipient1 Name"] || row?.["Recipient Reference"] || "").trim();
    const rowEmail = String(row?.email || row?.["Recipient1 Contact"] || "").trim();

    return selectedUsers.find((user: any) => {
      const subjectId = this.getUserSubjectId(user);
      const name = this.getUserDisplayName(user);
      const email = this.getUserEmail(user);

      return (rowSubjectId && subjectId === rowSubjectId) ||
        (rowEmail && email === rowEmail) ||
        (rowName && name === rowName);
    });
  }

  // This function runs whenever the user checks or unchecks a name in the dropdown
  onRecipientsChanged(selected: any[], cardKey: RecipientCardKey) {
    const card = this.recipientCards[cardKey];
    this.selectedRecipients = selected;
    card.selectedUsers = selected;

    // Automatically populate recipient CSV data from selected users
    if (card.appUserSelected && selected.length > 0) {
      card.appUserCsvData = selected.map((user: any) => {
        const fullName = this.getUserDisplayName(user);
        const userSubjectId = this.getUserSubjectId(user);

        // Use email for Recipient1 Contact
        let recipientContact = this.getUserEmail(user);

        // If email is still empty, try to get it from the original user list
        if (!recipientContact && this.originalUserList.length > 0) {
          const originalUser = this.findOriginalUser(user);
          if (originalUser) {
            recipientContact = originalUser.email || "";
          }
        }

        return {
          __recipientSource: "applicationUser",
          userSubjectId,
          userName: fullName,
          email: recipientContact,
          "Recipient Reference": fullName,
          "Recipient1 Name": fullName,
          "Recipient1 Contact": recipientContact,
          "Recipient2 Name": "",
          "Recipient2 Contact": "",
        };
      });
    } else if (!card.appUserSelected) {
      // Clear the app user data if app user checkbox is unchecked
      card.appUserCsvData = [];
    }
  }

  onSaveClick(): void {
    this.showSaveModal = true;
  }

	loadExistingNotificationConfig(logsheetId: number): void {
		// Fetch all existing notification configs for this logsheet (supporting multiple notification types)
		this.notificationConfigService.getByLogSheetTemplateIdBatch(logsheetId).subscribe({
			next: (configs: any[]) => {
				if (!configs || configs.length === 0) {
					// No config exists - this is create mode
					this.isEditMode = false;
					return;
				}

				// Config exists - populate the form with existing data
				this.isEditMode = true;

				// Store ALL config IDs for updates
				const configIds = configs.map((c: any) => c.notificationConfigId);
				this.existingConfigIds = configIds; // Store all IDs
				this.existingConfigId = configIds[0]; // Also store first for backward compatibility
				
				// Process each config (one for each notification type)
				configs.forEach((config: any) => {
					// Parse notification channels
					const channels = config.notificationChannels.split(',').map((c: string) => c.trim());
					
					// Set notification type and populate UI state
					const notificationType = config.notificationType;
					let cardKey: RecipientCardKey = 'perItem';
					
					if (notificationType === 'Per Item Notification') {
						this.perItemEnabled = true;
						cardKey = 'perItem';
						this.perItemChannels = { 
							sms: channels.includes('SMS'),
							email: channels.includes('Email'),
							whatsapp: channels.includes('WhatsApp') ? true : undefined
						};
					} else if (notificationType === 'Form Submission Notification') {
						this.formSubmissionEnabled = true;
						cardKey = 'formSubmission';
						this.formSubmissionChannels = { 
							sms: channels.includes('SMS'),
							email: channels.includes('Email'),
							whatsapp: channels.includes('WhatsApp') ? true : undefined
						};
					} else if (notificationType === 'Reminder Notification') {
						this.reminderEnabled = true;
						cardKey = 'reminder';
						this.reminderChannels = { 
							sms: channels.includes('SMS'),
							email: channels.includes('Email'),
							whatsapp: channels.includes('WhatsApp') ? true : undefined
						};
					}
					
					const card = this.recipientCards[cardKey];
					
					// Store the config ID for this card so we can update it specifically
					(card as any).notificationConfigId = config.notificationConfigId;
					
					// Parse recipient types
					const recipientTypes = config.recipientType.split(',').map((r: string) => r.trim());
					
					// Set recipient type toggles for this card
					card.appUserSelected = recipientTypes.includes('Application User');
					card.uploadedRefFileSelected = recipientTypes.includes('Uploaded Reference File');
					
					// If CSV file was uploaded, set the filename
					if (config.csvFileName) {
						card.uploadedFileName = config.csvFileName;
						card.csvValidated = true;
					}
					
					// Populate recipients data
					if (config.recipients && config.recipients.length > 0) {
						// If app user is selected, populate selectedUsers from recipients
						if (card.appUserSelected) {
							const applicationUserRecipients = config.recipients.filter(
                (r: any) => r.userSubjectId || r.userId,
              );

							if (applicationUserRecipients.length > 0) {
								const userSubjectIds = applicationUserRecipients
                  .map((r: any) => String(r.userSubjectId || '').trim())
                  .filter((subjectId: string) => subjectId.length > 0);
                const legacyUserIds = applicationUserRecipients
                  .filter((r: any) => !r.userSubjectId && r.userId)
                  .map((r: any) => r.userId);

								// First, try to select users that match the userIds from dbUsers
								let selectedUsers = this.dbUsers.filter((u: any) => {
									const subjectId = this.getUserSubjectId(u);
									const userId = u.userId || u.id;
									return subjectId
                    ? userSubjectIds.includes(subjectId)
                    : legacyUserIds.includes(userId);
								});
								
								// If dbUsers is empty or we didn't find all matches, construct users from the API response
								// This handles cases where dbUsers hasn't loaded yet or isn't available
								if (selectedUsers.length === 0 && applicationUserRecipients.length > 0) {
									selectedUsers = applicationUserRecipients.map((recipient: any) => ({
										id: recipient.userSubjectId || recipient.userId,
										userId: recipient.userId,
                    userSubjectId: recipient.userSubjectId,
                    subjectId: recipient.userSubjectId,
										name: recipient.userName || '',
										firstName: recipient.userName?.split(' ')[0] || '',
										lastName: recipient.userName?.split(' ').slice(1).join(' ') || '',
										email: recipient.email || recipient.recipient1Contact || '',
										phone: '',
										...recipient // Spread the entire recipient object to preserve all data
									}));
								} else if (selectedUsers.length < applicationUserRecipients.length) {
									// Partial match - some users from dbUsers, some from API response
									// Create a map of found user IDs
									const foundUserKeys = new Set(
                    selectedUsers.map((u: any) => this.getUserSubjectId(u) || String(u.userId || u.id))
                  );
									// Add any missing users from the API response
									const missingUsers = applicationUserRecipients
										.filter((r: any) => !foundUserKeys.has(r.userSubjectId || String(r.userId)))
										.map((recipient: any) => ({
											id: recipient.userSubjectId || recipient.userId,
											userId: recipient.userId,
                      userSubjectId: recipient.userSubjectId,
                      subjectId: recipient.userSubjectId,
											name: recipient.userName || '',
											firstName: recipient.userName?.split(' ')[0] || '',
											lastName: recipient.userName?.split(' ').slice(1).join(' ') || '',
											email: recipient.email || recipient.recipient1Contact || '',
											phone: '',
											...recipient
										}));
									selectedUsers = [...selectedUsers, ...missingUsers];
								}

								// Update card's selected users
								card.selectedUsers = selectedUsers;
								
								// Populate appUserCsvData from the matched users
								card.appUserCsvData = selectedUsers.map((user: any) => {
									// Find the corresponding recipient data
									const userSubjectId = this.getUserSubjectId(user);
									const recipientData = applicationUserRecipients.find((r: any) =>
                    r.userSubjectId
                      ? r.userSubjectId === userSubjectId
                      : r.userId === (user.userId || user.id)
                  );
                  const fullName = this.getUserDisplayName(user);
                  const email = recipientData?.email || recipientData?.recipient1Contact || this.getUserEmail(user);
									return {
                    __recipientSource: "applicationUser",
                    userSubjectId,
                    userName: fullName,
                    email,
										'Recipient Reference': fullName,
										'Recipient1 Name': fullName,
										'Recipient1 Contact': email,
										'Recipient2 Name': recipientData?.recipient2Name || '',
										'Recipient2 Contact': recipientData?.recipient2Contact || ''
									};
								});
							}
							
							// Also preserve any uploaded CSV data that's not user-based
							const csvOnlyRecipients = config.recipients.filter((r: any) => !r.userSubjectId && !r.userId);
							if (csvOnlyRecipients.length > 0) {
								card.uploadedCsvData = csvOnlyRecipients.map((recipient: any) => ({
									'Default Field name': recipient.defaultFieldName || '',
									'Recipient Reference': recipient.defaultFieldName || '',
									'Recipient1 Name': recipient.recipient1Name || '',
									'Recipient1 Contact': recipient.recipient1Contact || '',
									'Recipient2 Name': recipient.recipient2Name || '',
									'Recipient2 Contact': recipient.recipient2Contact || ''
								}));
							}
						} else {
							// No app users selected, all recipients are from uploaded CSV
							card.uploadedCsvData = config.recipients.map((recipient: any) => ({
								'Default Field name': recipient.defaultFieldName || '',
								'Recipient Reference': recipient.defaultFieldName || '',
								'Recipient1 Name': recipient.recipient1Name || '',
								'Recipient1 Contact': recipient.recipient1Contact || '',
								'Recipient2 Name': recipient.recipient2Name || '',
								'Recipient2 Contact': recipient.recipient2Contact || ''
							}));
						}
					}
				});
				
				// Ensure first enabled card is expanded
				this.ensureFirstRecipientCardExpanded();
			},
			error: (err: any) => {
				// Try fallback to single config endpoint (for backward compatibility)
				console.log('Batch endpoint not available, trying single endpoint:', err);
				this.notificationConfigService.getByLogSheetTemplateId(logsheetId).subscribe({
					next: (config: any) => {
						this.isEditMode = true;
						this.existingConfigId = config.notificationConfigId;
						this.existingConfigIds = [config.notificationConfigId]; // Store as array for consistency
						this.loadSingleConfig(config);
					},
					error: (fallbackErr: any) => {
						// No config exists - this is create mode
						this.isEditMode = false;
					}
				});
			}
		});
	}

  private loadSingleConfig(config: any): void {
    // Helper method to load a single config (used for backward compatibility)

    // Parse notification channels
    const channels = config.notificationChannels
      .split(",")
      .map((c: string) => c.trim());
    this.smsSelected = channels.includes("SMS");
    this.whatsappSelected = channels.includes("WhatsApp");
    this.emailSelected = channels.includes("Email");

    // Set notification type and populate UI state
    const notificationType = config.notificationType;
    let cardKey: RecipientCardKey = "perItem";

    if (notificationType === "Per Item Notification") {
      this.perItemEnabled = true;
      this.perItemChannels = {
        sms: channels.includes("SMS"),
        email: channels.includes("Email"),
        whatsapp: channels.includes("WhatsApp") ? true : undefined,
      };
    } else if (notificationType === "Form Submission Notification") {
      this.formSubmissionEnabled = true;
      this.formSubmissionChannels = {
        sms: channels.includes("SMS"),
        email: channels.includes("Email"),
        whatsapp: channels.includes("WhatsApp") ? true : undefined,
      };
    } else if (notificationType === "Reminder Notification") {
      this.reminderEnabled = true;
      this.reminderChannels = {
        sms: channels.includes("SMS"),
        email: channels.includes("Email"),
        whatsapp: channels.includes("WhatsApp") ? true : undefined,
      };
    }

    // Parse recipient types
    const recipientTypes = config.recipientType
      .split(",")
      .map((r: string) => r.trim());

    const card = this.recipientCards[cardKey];

    // Set recipient type toggles for this card
    card.appUserSelected = recipientTypes.includes("Application User");
    card.uploadedRefFileSelected = recipientTypes.includes(
      "Uploaded Reference File",
    );

    // If CSV file was uploaded, set the filename
    if (config.csvFileName) {
      card.uploadedFileName = config.csvFileName;
      card.csvValidated = true;
    }

    // Populate recipients data
    if (config.recipients && config.recipients.length > 0) {
      if (card.appUserSelected) {
        const applicationUserRecipients = config.recipients.filter(
          (r: any) => r.userSubjectId || r.userId,
        );

        if (applicationUserRecipients.length > 0) {
          const userSubjectIds = applicationUserRecipients
            .map((r: any) => String(r.userSubjectId || "").trim())
            .filter((subjectId: string) => subjectId.length > 0);
          const legacyUserIds = applicationUserRecipients
            .filter((r: any) => !r.userSubjectId && r.userId)
            .map((r: any) => r.userId);
          let selectedUsers = this.dbUsers.filter((u: any) => {
            const subjectId = this.getUserSubjectId(u);
            const userId = u.userId || u.id;
            return subjectId
              ? userSubjectIds.includes(subjectId)
              : legacyUserIds.includes(userId);
          });

          if (selectedUsers.length < applicationUserRecipients.length) {
            const foundUserKeys = new Set(
              selectedUsers.map((u: any) => this.getUserSubjectId(u) || String(u.userId || u.id)),
            );
            const missingUsers = applicationUserRecipients
              .filter((recipient: any) => !foundUserKeys.has(recipient.userSubjectId || String(recipient.userId)))
              .map((recipient: any) => ({
                id: recipient.userSubjectId || recipient.userId,
                userId: recipient.userId,
                userSubjectId: recipient.userSubjectId,
                subjectId: recipient.userSubjectId,
                name: recipient.userName || "",
                firstName: recipient.userName?.split(" ")[0] || "",
                lastName: recipient.userName?.split(" ").slice(1).join(" ") || "",
                email: recipient.email || recipient.recipient1Contact || "",
                phone: "",
                ...recipient,
              }));
            selectedUsers = [...selectedUsers, ...missingUsers];
          }

          card.selectedUsers = selectedUsers;
          card.appUserCsvData = selectedUsers.map((user: any) => {
            const userSubjectId = this.getUserSubjectId(user);
            const recipientData = applicationUserRecipients.find(
              (r: any) =>
                r.userSubjectId
                  ? r.userSubjectId === userSubjectId
                  : r.userId === (user.userId || user.id),
            );
            const fullName = this.getUserDisplayName(user);
            const email = recipientData?.email || recipientData?.recipient1Contact || this.getUserEmail(user);
            return {
              __recipientSource: "applicationUser",
              userSubjectId,
              userName: fullName,
              email,
              "Recipient Reference": fullName,
              "Recipient1 Name": fullName,
              "Recipient1 Contact": email,
              "Recipient2 Name": recipientData?.recipient2Name || "",
              "Recipient2 Contact": recipientData?.recipient2Contact || "",
            };
          });
        }

        const csvOnlyRecipients = config.recipients.filter(
          (r: any) => !r.userSubjectId && !r.userId,
        );
        if (csvOnlyRecipients.length > 0) {
          card.uploadedCsvData = csvOnlyRecipients.map((recipient: any) => ({
            "Default Field name": recipient.defaultFieldName || "",
            "Recipient Reference": recipient.defaultFieldName || "",
            "Recipient1 Name": recipient.recipient1Name || "",
            "Recipient1 Contact": recipient.recipient1Contact || "",
            "Recipient2 Name": recipient.recipient2Name || "",
            "Recipient2 Contact": recipient.recipient2Contact || "",
          }));
        }
      } else {
        card.uploadedCsvData = config.recipients.map((recipient: any) => ({
          "Default Field name": recipient.defaultFieldName || "",
          "Recipient Reference": recipient.defaultFieldName || "",
          "Recipient1 Name": recipient.recipient1Name || "",
          "Recipient1 Contact": recipient.recipient1Contact || "",
          "Recipient2 Name": recipient.recipient2Name || "",
          "Recipient2 Contact": recipient.recipient2Contact || "",
        }));
      }
    }

    this.ensureFirstRecipientCardExpanded();
  }

  handleSaveConfirm(): void {
    this.showSaveModal = false;

    if (!this.logsheetId || this.logsheetId <= 0) {
      this.toastService.showToast = true;
      this.toastService.isError = true;
      this.toastService.toastText =
        "Please select a valid form before saving.";

      setTimeout(() => {
        this.toastService.showToast = false;
      }, 5000);

      return;
    }

    // Show loaders
    this.loaderService.showLoader();

    const order: RecipientCardKey[] = ["perItem", "formSubmission", "reminder"];
    const enabledKeys = order.filter((key) => {
      if (key === "perItem") return this.perItemEnabled;
      if (key === "formSubmission") return this.formSubmissionEnabled;
      return this.reminderEnabled;
    });
    let hasMissingApplicationUserSubject = false;
    const configs: CreateNotificationConfig[] = enabledKeys.map((key) => {
      const card = this.recipientCards[key];

      const channels: string[] = [];
      const cardChannels =
        key === "perItem"
          ? this.perItemChannels
          : key === "formSubmission"
            ? this.formSubmissionChannels
            : this.reminderChannels;

      if (cardChannels.sms) channels.push("SMS");
      // if (cardChannels.whatsapp) channels.push("WhatsApp");
      if (cardChannels.email) channels.push("Email");

      const notificationType =
        key === "perItem"
          ? "Per Item Notification"
          : key === "formSubmission"
            ? "Form Submission Notification"
            : "Reminder Notification";

      const recipientTypeSet = new Set<string>();
      let allCsvData: any[] = [];

      if (card.appUserSelected) {
        recipientTypeSet.add("Application User");
        allCsvData = [
          ...allCsvData,
          ...card.appUserCsvData.map((row) => ({
            ...row,
            userSubjectId:
              row.userSubjectId ||
              this.getUserSubjectId(this.findSelectedUserForRecipientRow(row, card.selectedUsers)),
            __recipientSource: "applicationUser",
          })),
        ];
      }

      const invalidApplicationUserRow = allCsvData.find((row) =>
        this.isApplicationUserRow(row) &&
        !String(row.userSubjectId || "").trim()
      );

      if (invalidApplicationUserRow) {
        hasMissingApplicationUserSubject = true;
      }

      if (card.uploadedRefFileSelected && card.uploadedCsvData.length > 0) {
        recipientTypeSet.add("Uploaded Reference File");
        allCsvData = [...allCsvData, ...card.uploadedCsvData];
      }

      const recipients: NotificationRecipient[] = allCsvData.map((row) => {
        const recipientObj: NotificationRecipient = {
          defaultFieldName:
            row["Recipient Reference"] || row["Default Field name"] || "",
          recipient1Name: row["Recipient1 Name"] || "",
          recipient1Contact: row["Recipient1 Contact"] || "",
          recipient2Name: row["Recipient2 Name"] || "",
          recipient2Contact: row["Recipient2 Contact"] || "",
        };

        if (this.isApplicationUserRow(row)) {
          recipientObj.userSubjectId = String(row.userSubjectId || "").trim();
          recipientObj.userName = row.userName || row["Recipient1 Name"] || row["Recipient Reference"] || "";
          recipientObj.email = row.email || row["Recipient1 Contact"] || "";
        }

        return recipientObj;
      });

      return {
        logSheetTemplateId: this.logsheetId,
        notificationChannels: channels.join(","),
        notificationType: notificationType,
        recipientType: Array.from(recipientTypeSet).join(", "),
        csvFileName: card.uploadedFileName || undefined,
        recipients: recipients,
      };
    });

    const missingSubjectConfig = configs.find((config) =>
      config.recipientType.includes("Application User") &&
      config.recipients.some((recipient) =>
        recipient.userSubjectId !== undefined &&
        !recipient.userSubjectId?.trim()
      )
    );

    if (hasMissingApplicationUserSubject || missingSubjectConfig) {
      this.loaderService.hideLoader();
      this.toastService.showToast = true;
      this.toastService.isError = true;
      this.toastService.toastText =
        "Application User recipients require a Keycloak user subject. Please reselect the user and try again.";

      setTimeout(() => {
        this.toastService.showToast = false;
      }, 5000);

      return;
    }

    // Determine whether to create or update
    if (this.isEditMode && this.existingConfigIds.length > 0) {
      // Update existing configuration - Delete ALL old ones and create new ones

      // Delete all existing configs in sequence
      let deleteIndex = 0;
      const deleteNext = () => {
        if (deleteIndex < this.existingConfigIds.length) {
          const configIdToDelete = this.existingConfigIds[deleteIndex];
          deleteIndex++;

          this.notificationConfigService.delete(configIdToDelete).subscribe({
            next: (deleteResponse: any) => {
              deleteNext(); // Delete the next one
            },
            error: (deleteError: any) => {
              console.error(
                `Error deleting config ID ${configIdToDelete}:`,
                deleteError,
              );
              this.loaderService.hideLoader();

              this.toastService.showToast = true;
              this.toastService.isError = true;
              this.toastService.toastText = `Failed to delete old notification config (ID: ${configIdToDelete}).`;

              setTimeout(() => {
                this.toastService.showToast = false;
              }, 5000);
            },
          });
        } else {
          // All deletions complete, now create new configs
          this.notificationConfigService.create(configs).subscribe({
            next: (createResponse: any) => {
              this.loaderService.hideLoader();

              // Show success toast - display immediately before navigation
              this.toastService.showToast = true;
              this.toastService.isError = false;
              this.toastService.toastText =
                "Notification configuration updated successfully.";

              // Auto-hide toast after 5 seconds
              setTimeout(() => {
                this.toastService.showToast = false;
              }, 5000);

              // Navigate back to logsheets with dropdown selections preserved
              this.router.navigateByUrl("settings/forms", {
                state: {
                  selectedValues: { ...this.selectedValues },
                  firstRender: true,
                },
              });
            },
            error: (createError: any) => {
              console.error("Create error after deletion:", createError);
              console.error("Create error response:", createError.error);
              this.loaderService.hideLoader();

              // Display error toast immediately on the current page
              this.toastService.showToast = true;
              this.toastService.isError = true;
              this.toastService.toastText =
                "Notification configuration update failed while creating new configs.";

              // Auto-hide toast after 5 seconds
              setTimeout(() => {
                this.toastService.showToast = false;
              }, 5000);
            },
          });
        }
      };

      // Start the deletion chain
      deleteNext();
    } else {
      // Create new configuration

      // Save to database
      this.notificationConfigService.create(configs).subscribe({
        next: (response: any) => {
          this.loaderService.hideLoader();

          // Show success toast - display immediately before navigation
          this.toastService.showToast = true;
          this.toastService.isError = false;
          this.toastService.toastText =
            "Notification configuration saved successfully.";

          // Auto-hide toast after 5 seconds
          setTimeout(() => {
            this.toastService.showToast = false;
          }, 5000);

          // Navigate back to logsheets with dropdown selections preserved
          this.router.navigateByUrl("settings/forms", {
            state: {
              selectedValues: { ...this.selectedValues },
              firstRender: true,
            },
          });
        },
        error: (error: any) => {
          console.error("Save error:", error);
          this.loaderService.hideLoader();

          // Display error toast immediately on the current page
          this.toastService.showToast = true;
          this.toastService.isError = true;
          this.toastService.toastText =
            "Notification configuration save failed.";

          // Auto-hide toast after 5 seconds
          setTimeout(() => {
            this.toastService.showToast = false;
          }, 5000);

          // Do NOT navigate - keep user on the current page so they can fix the issue
          // Note: We handle the toast display directly here instead of using errorService
          // to ensure the error message displays properly even in offline scenarios
        },
      });
    }
  }

  handleSaveCancel(): void {
    this.showSaveModal = false;
  }

  transferData() {
    this.firstRender = true;
    this.router.navigateByUrl("settings/forms", {
      state: {
        selectedValues: { ...this.selectedValues },
        firstRender: this.firstRender,
      },
    });
  }
  setActiveStep(step: number) {
    this.activeStep = step;
  }

  enableNextBtn() {
    if (
      (this.activeStep === 1 && this.isStep1Valid) ||
      (this.activeStep === 2 && this.isStep2Valid)
    ) {
      return true;
    }
    return false;
  }

  downloadCsvTemplate(cardKey?: string) {
    // Determine which template to download based on which card requested it
    let headers: string[];
    let filename: string;
    
    if (cardKey === 'perItem') {
      // Per Item Notification requires 5-column format with Recipient Reference
      headers = ["Recipient Reference", "Recipient1 Name", "Recipient1 Contact", "Recipient2 Name", "Recipient2 Contact"];
      filename = "notification_recipients_template (per item).csv";
    } else {
      // Form Submission and Reminder use simple 2-column format
      headers = ["Recipient Name", "Recipient Contact"];
      filename = "notification_recipients_template.csv";
    }
    
    const csvContent: string = headers.join(",");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // Validation helpers for multi-channel notification support (SMS + Email)
  isValidPhoneNumber(phoneNumber: string): boolean {
    // Allow blank entries
    if (!phoneNumber || phoneNumber.trim() === "") {
      return true;
    }
    // Check if it's 10 or 11 digits (supports both local and international format)
    const phoneRegex = /^\d{10,11}$/;
    return phoneRegex.test(phoneNumber.trim());
  }

  isValidEmail(email: string): boolean {
    if (!email || email.trim() === "") {
      return true;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  isValidContact(contact: string): boolean {
    if (!contact || contact.trim() === "") {
      return true;
    }
    // Contact can be either a valid phone number OR a valid email (for multi-channel support)
    return this.isValidPhoneNumber(contact) || this.isValidEmail(contact);
  }

  isValidName(name: string): boolean {
    // Allow blank entries
    if (!name || name.trim() === "") {
      return true;
    }
    // Check if it's a valid string (at least 1 character)
    return name.trim().length > 0;
  }

  validateCsvData(data: any[]): { valid: boolean; error?: string } {
    for (const row of data) {
      const dataPointName = row["Recipient Reference"];

      // If there's a datapoint name (row exists), Recipient1 Name and Recipient1 Contact are required
      if (dataPointName && dataPointName.trim()) {
        // Check Recipient1 Name (required)
        if (!row["Recipient1 Name"] || row["Recipient1 Name"].trim() === "") {
          return {
            valid: false,
            error: `Recipient1 Name is required for datapoint "${dataPointName}".`,
          };
        }

        // Check Recipient1 Contact (required) - must be valid phone (10-11 digits) OR valid email
        const recipient1Contact = row["Recipient1 Contact"];
        if (!recipient1Contact || recipient1Contact.trim() === "") {
          return {
            valid: false,
            error: `Recipient1 Contact is required for datapoint "${dataPointName}".`,
          };
        }
        if (!this.isValidContact(recipient1Contact)) {
          return {
            valid: false,
            error: `Invalid Recipient1 Contact for datapoint "${dataPointName}": "${recipient1Contact}". Must be a valid email or 10-11 digit phone number.`,
          };
        }

        // Check Recipient2 Name and Contact (both optional, but if one is provided, the other is required)
        const recipient2Name = row["Recipient2 Name"];
        const recipient2Contact = row["Recipient2 Contact"];

        // If Recipient2 Contact is provided, Recipient2 Name is required
        if (recipient2Contact && recipient2Contact.trim() !== "") {
          if (!recipient2Name || recipient2Name.trim() === "") {
            return {
              valid: false,
              error: `Recipient2 Name is required when Recipient2 Contact is provided for datapoint "${dataPointName}".`,
            };
          }
          // Validate the contact (phone or email)
          if (!this.isValidContact(recipient2Contact)) {
            return {
              valid: false,
              error: `Invalid Recipient2 Contact for datapoint "${dataPointName}": "${recipient2Contact}". Must be a valid email or 10-11 digit phone number.`,
            };
          }
        }

        // If Recipient2 Name is provided, Recipient2 Contact is required
        if (recipient2Name && recipient2Name.trim() !== "") {
          if (!recipient2Contact || recipient2Contact.trim() === "") {
            return {
              valid: false,
              error: `Recipient2 Contact is required when Recipient2 Name is provided for datapoint "${dataPointName}".`,
            };
          }
        }
      }
    }
    return { valid: true };
  }

  onFileSelected(event: Event, cardKey: RecipientCardKey): void {
    const card = this.recipientCards[cardKey];
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      card.validationMessage = "";
      card.csvValidated = false;
      card.uploadedFileName = "";
      this.resetUploadProgress(card);
      return;
    }

    const file = files[0];
    // Set filename immediately and reset validation state for new file selection
    card.uploadedFileName = file.name;
    card.csvValidated = false;
    card.validationMessage = "";

    this.startUploadProgress(card);

    // Show loading spinner
    this.loaderService.showLoader();

    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const csv = e.target.result;
        const lines = csv.split("\n");

        if (lines.length < 2) {
          card.validationMessage =
            "CSV file is empty or contains only headers.";
          card.csvValidated = false;
          this.failUploadProgress(card);
          // Hide loading spinner
          this.loaderService.hideLoader();
          // Reset file input to allow re-selection of the same file
          input.value = "";
          return;
        }

        // Parse headers
        const headers = lines[0].split(",").map((h: string) => h.trim());

        // CSV Format Detection: Support both simple and complex formats
        // Simple format: "Recipient Name, Recipient Contact" (user-friendly, 2 columns)
        // Complex format: "Recipient Reference, Recipient1 Name, Recipient1 Contact, Recipient2 Name, Recipient2 Contact" (5 columns, legacy)
        const simpleHeaders = ["Recipient Name", "Recipient Contact"];
        const complexHeaders = [
          "Recipient Reference",
          "Recipient1 Name",
          "Recipient1 Contact",
          "Recipient2 Name",
          "Recipient2 Contact",
        ];

        const isSimpleFormat = simpleHeaders.every((h: string) =>
          headers.includes(h),
        );
        const isComplexFormat = complexHeaders.every((h: string) =>
          headers.includes(h),
        );

        // Check if either format is valid
        if (!isSimpleFormat && !isComplexFormat) {
          card.validationMessage = `Invalid CSV format. Please use either:\nSimple format: ${simpleHeaders.join(", ")}\nOR\nComplex format: ${complexHeaders.join(", ")}`;
          card.csvValidated = false;
          this.failUploadProgress(card);
          // Hide loading spinner
          this.loaderService.hideLoader();
          // Reset file input to allow re-selection of the same file
          input.value = "";
          return;
        }

        // Parse data rows
        const data: any[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line === "") continue; // Skip empty lines

          const values = line.split(",").map((v: string) => v.trim());
          const row: any = {};

          headers.forEach((header: string, index: number) => {
            row[header] = values[index] || "";
          });

          // CSV Format Transformation: If using simple format, auto-transform to complex format for backend compatibility
          // This allows users to use the simpler 2-column format while maintaining backward compatibility
          if (isSimpleFormat && !isComplexFormat) {
            const recipientName = row["Recipient Name"] || "";
            const recipientContact = row["Recipient Contact"] || "";

            const transformedRow = {
              "Recipient Reference": recipientName,
              "Recipient1 Name": recipientName,
              "Recipient1 Contact": recipientContact,
              "Recipient2 Name": "",
              "Recipient2 Contact": "",
            };

            data.push(transformedRow);
          } else {
            data.push(row);
          }
        }

        // Validate CSV data
        const validation = this.validateCsvData(data);
        if (!validation.valid) {
          card.validationMessage = validation.error || "CSV validation failed.";
          card.csvValidated = false;
          this.failUploadProgress(card);
          // Hide loading spinner
          this.loaderService.hideLoader();
          // Reset file input to allow re-selection of the same file
          input.value = "";
          return;
        }

        // Success - store the parsed data
        card.uploadedCsvData = data;
        card.validationMessage = "File uploaded successfully.";
        card.csvValidated = true;
        this.completeUploadProgress(card);
        // Hide loading spinner
        this.loaderService.hideLoader();

        // Reset file input to allow re-selection of the same file
        input.value = "";
      } catch (error: any) {
        card.validationMessage = `Error parsing CSV file: ${error.message}`;
        card.csvValidated = false;
        this.failUploadProgress(card);
        // Hide loading spinner
        this.loaderService.hideLoader();
        // Reset file input to allow re-selection of the same file
        input.value = "";
      }
    };

    reader.onerror = () => {
      card.validationMessage = "Error reading file. Please try again.";
      card.csvValidated = false;
      this.failUploadProgress(card);
      // Hide loading spinner
      this.loaderService.hideLoader();
    };
    reader.readAsText(file);
  }
}
